import React from "react";
import {useMessages} from "i18n";
import {VisuallyHiddenLiveRegion} from "components/Accessibility.styles";
import {EmailSuggestionChip} from "components/emailSuggestion/EmailSuggestionChip";
import type {PaidPlanId} from "pricing/planCatalogue";
import {ContactsRecipientPicker} from "./ContactsRecipientPicker";
import {ChipInput, ChipsRow, RecipientChip, UnderlineField} from "./FormField";
import {RecipientNudgePopover} from "./RecipientNudgePopover";
import type {RecipientsFieldVM} from "./useRecipientsField";
import {PickerActions, RecipientChipWrap, RecipientStatus} from "./TransferPanel.styles";
import {FieldHint, RecipientField, Section, SectionHeader, SectionLabel} from "./OptionsSheet.styles";

interface Props {
	vm: RecipientsFieldVM;
	/** The section's label, in the words of the caller's direction: a send emails people, a request notifies them. */
	label: string;
	/** Opens an upgrade surface on the tier the full-list nudge names (ui#2524); the caller decides which. */
	onUpgradeTo: (plan: PaidPlanId) => void;
	/** That tier's monthly price, already formatted, for the nudge's line. */
	monthlyPriceOf: (plan: PaidPlanId) => string;
	/** A line under the field saying what leaving it empty does, where that is worth saying. */
	hint?: string;
}

/**
 * The unified recipients field (ui#210–#213): email chips, group chips, the contacts picker, the typed-address
 * suggestion and the full-list nudge, as one section. Shared by the send Options sheet and the request mode's
 * (kitecorp/transferify-ui#3140), so the two directions pick people the same way. A dumb view over
 * {@link RecipientsFieldVM}.
 */
export function RecipientsSection({vm, label, onUpgradeTo, monthlyPriceOf, hint}: Props) {
	const {sender, pricing, common} = useMessages();
	const recipientStatusId = React.useId();
	const recipientUpgradeTipId = React.useId();
	// Held as a const so the render below narrows it once: a property read cannot stay narrowed inside the
	// nudge's own click handler.
	const recipientUpgrade = vm.recip.upgrade;

	const onRecipKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();
			vm.commitRecipient();
		}
	};

	return (
		<Section>
			<SectionHeader>
				<SectionLabel>{label}</SectionLabel>
				{/* The inline offer for a FULL list (ui#2524). Whether it is owed at all is `vm.recip.upgrade`'s
				  * answer — a React-free rule — so this row states no cap and no tier ladder of its own. */}
				{recipientUpgrade !== null && (
					<RecipientNudgePopover
						tipId={recipientUpgradeTipId}
						message={sender.recipients.upgradeLine(
							recipientUpgrade.recipients,
							pricing.planNames[recipientUpgrade.target],
							monthlyPriceOf(recipientUpgrade.target),
						)}
						upgradeLabel={common.upgrade}
						upgradeAria={sender.limitCard.upgradeTo(pricing.planNames[recipientUpgrade.target])}
						onUpgrade={() => onUpgradeTo(recipientUpgrade.target)}
					/>
				)}
			</SectionHeader>
			<RecipientField>
				<UnderlineField>
					<ChipsRow>
						{vm.recipients.map((email, i) => (
							<RecipientChipWrap key={`email:${email}`}>
								<RecipientChip email={email} onRemove={() => vm.removeRecipient(i)}/>
							</RecipientChipWrap>
						))}
						{vm.selectedGroups.map(group => (
							<RecipientChipWrap key={`group:${group.id}`}>
								<RecipientChip
									email={sender.recipients.selectedGroup(group.name, group.contacts.length)}
									onRemove={() => vm.removeGroup(group.id)}
								/>
							</RecipientChipWrap>
						))}
						<ChipInput
							type="email"
							placeholder={vm.recip.placeholder}
							value={vm.recipInput}
							readOnly={!vm.recip.canAdd}
							onChange={e => vm.setRecipInput(e.target.value)}
							onKeyDown={onRecipKey}
							onBlur={vm.commitRecipient}
							aria-label={sender.recipients.addAria}
							aria-describedby={recipientStatusId}
							aria-invalid={vm.recipientError !== null}
						/>
					</ChipsRow>
				</UnderlineField>
				{/* ui#1959: the suggestion is about the address BEING TYPED, before it is committed as a chip —
				  * once committed it is a recipient the sender can remove and retype. */}
				<EmailSuggestionChip value={vm.recipInput} onAccept={vm.setRecipInput}/>
				<PickerActions>
					<ContactsRecipientPicker
						onConfirm={vm.addRecipients}
						onConfirmGroups={vm.selectGroups}
						selectedGroups={vm.selectedGroups}
						recipientError={vm.recipientError}
					/>
				</PickerActions>
				{/* Persistent polite live region (ui#211) — kept inside this section so the screen-reader
				  * announcement of the recipient limit/status is never dropped. */}
				<VisuallyHiddenLiveRegion id={recipientStatusId} role="status" aria-live="polite" aria-atomic="true">
					{vm.recip.statusMessage}
				</VisuallyHiddenLiveRegion>
				{vm.recip.statusMessage !== null && (
					<RecipientStatus aria-hidden="true">{vm.recip.statusMessage}</RecipientStatus>
				)}
				{hint !== undefined && <FieldHint>{hint}</FieldHint>}
			</RecipientField>
		</Section>
	);
}
